import React, { useEffect, useRef, useState } from 'react';
import { extent, max } from '@visx/vendor/d3-array';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { curveNatural } from '@visx/curve';
import { AxisBottom } from '@visx/axis';

import BaseTypography from 'src/components/Text/BaseTypography';
import { AnalyticsDailyStats } from 'src/types/analytics';

export type ChartData = Pick<AnalyticsDailyStats, 'total' | 'date'>;

// Data accessors
const getX = (d: ChartData) => d.date;
const getY = (d: ChartData) => d.total;

const isDateTuple = (tuple: [unknown, unknown]): tuple is [Date, Date] => {
  return tuple.every((d) => d instanceof Date);
};

const ensureDateTuple = (tuple: [unknown, unknown]): [Date, Date] => {
  if (!isDateTuple(tuple)) {
    throw new Error('Invalid date tuple');
  }
  return tuple;
};

export function Chart({ data }: { data: ChartData[] }) {
  // Axis scales
  const getScale = () => {
    const y = max(data, getY);
    if (typeof y !== 'number') {
      throw new Error('Value missing for y axis');
    }
    return {
      x: scaleUtc<number>({
        domain: ensureDateTuple(extent(data, getX)),
      }),
      y: scaleLinear<number>({
        domain: [0, y],
      }),
    };
  };

  const sizeReference = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(getScale());

  useEffect(() => {
    const handleResize = () => {
      if (!sizeReference.current) return;
      const { width, height } = sizeReference.current.getBoundingClientRect();
      const updatedScale = getScale();
      updatedScale.x.range([0, width]);
      updatedScale.y.range([height - 24, 0]);
      setScale(updatedScale);
    };
    addEventListener('resize', handleResize);
    handleResize();
    return () => removeEventListener('resize', handleResize);
  }, [sizeReference]);

  return (
    <div className="text-current h-35 w-full" ref={sizeReference}>
      <div className="flex pb-5">
        <div className="w-6 pr-4 box-content">
          <div className="relative w-full h-full mt-[-0.5rem] text-gray-500">
            <BaseTypography
              className="leading-none absolute top-0 right-0"
              fontType="13Regular"
            >
              100
            </BaseTypography>
            <BaseTypography
              className="leading-none absolute top-[50%] right-0"
              fontType="13Regular"
            >
              50
            </BaseTypography>
            <BaseTypography
              className="leading-none absolute top-[100%] right-0"
              fontType="13Regular"
            >
              0
            </BaseTypography>
          </div>
        </div>
        <svg className="w-full h-full border-y border-y-current border-solid border-x-0">
          <Group>
            <line x1="0%" x2="100%" y1="50%" y2="50%" stroke="currentColor" />
            <LinePath<ChartData>
              curve={curveNatural}
              data={data}
              shapeRendering="geometricPrecision"
              stroke="currentColor"
              strokeOpacity={1}
              strokeWidth={1}
              x={(d) => {
                return scale.x(getX(d)) ?? 0;
              }}
              y={(d) => {
                return scale.y(getY(d)) ?? 0;
              }}
            />
          </Group>
        </svg>
      </div>
      <svg className="w-full h-5 text-gray-500">
        <AxisBottom
          axisClassName="fill-current"
          axisLineClassName="stroke-current"
          hideTicks
          hideAxisLine
          // Go with a low number for the empty state, we can expand
          // it when real data is present.
          numTicks={4}
          scale={scale.x}
          tickComponent={({ x, formattedValue }) => (
            <text
              className="fill-current"
              x={x}
              y={6} // No idea why, it just looks right this way.
              dy=".33em"
              fontSize={13}
              textAnchor="middle"
            >
              {formattedValue}
            </text>
          )}
        />
      </svg>
    </div>
  );
}
